<template>
  <div class="pj-card-banner mt-3">
    <div
      class="pj-card-banner-container"
      @click.stop="openPayJoyCardModal"
      :class="payjoyCardBannerColor"
      :style="!showLinkToOpenModal ? 'cursor:default;' : ''"
    >
      <div class="d-flex flex-row bd-highlight mb-3 align-items-center">
        <div class="pj-card-preview">
          <img :src="payjoyCardBannerImage" alt="PayJoy Card" />
        </div>
        <div class="p-2 pt-3 pj-card-promo-details">
          <div class="row align-items-center">
            <div class="col-12">
              <h5>{{ payjoyCardBannerTitle }}</h5>
            </div>
            <div class="col-12">
              <div class="row justify-content-between align-items-center">
                <div class="col-12 col-md">
                  <p>{{ payjoyCardBannerDescription }}</p>
                </div>
                <div
                  v-if="showLinkToOpenModal"
                  class="col-12 col-md text-md-end banner-cta"
                >
                  {{ payjoyCardBannerCta }}
                  <i class="fa-solid fa-angle-right"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PayJoyCardBannerModal
      :show="showPayJoyCardModal"
      :credit-line-limit="creditLineLimitDisplay"
      :credit-line-term-in-weeks="props.creditLineTermInWeeks"
      @close-modal="closePayJoyCardModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import useCurrency from "@/apps/merchant3/shared-hooks/useCurrency";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import PayJoyCardBannerModal from "./PayJoyCardBannerModal.vue";
const { getCurrency } = useCurrency();

const props = defineProps({
  creditLineLimit: {
    type: Number,
    default: 0,
  },
  creditLineTermInWeeks: {
    type: Number,
    default: 0,
  },
  showLinkToOpenModal: {
    type: Boolean,
    default: true,
  },
  autoOpenModal: {
    type: Boolean,
    default: true,
  },
});
const creditLineLimit = ref(props.creditLineLimit);

const store = useStore();
const { t } = useI18n();
const currency = computed(() => {
  return getCurrency(store.state.merchant?.merchant?.country);
});
const isCardAvailable: Ref<boolean> = computed(
  () => creditLineLimit.value !== 0
);
const showPayJoyCardModal = ref<boolean>(
  props.autoOpenModal && props.showLinkToOpenModal && isCardAvailable.value
);
const creditLineLimitDisplay = computed(() => {
  return `${currency.value.sign}${creditLineLimit.value} ${currency.value.code}`;
});
const openPayJoyCardModal = () => {
  if (isCardAvailable.value) {
    showPayJoyCardModal.value = true;
    logEvent(EventLogs.Events.PAYJOY_CARD_BANNER_CLICKED);
  }
};
const closePayJoyCardModal = () => {
  showPayJoyCardModal.value = false;
};

const payjoyCardBannerTitle = computed(() =>
  isCardAvailable.value
    ? t("PAYJOY_CARD_BANNER_TITLE")
    : t("PAYJOY_NO_CARD_BANNER_TITLE")
);
const payjoyCardBannerDescription = computed(() =>
  isCardAvailable.value
    ? t("PAYJOY_CARD_BANNER_DESCRIPTION", {
        creditLineLimit: creditLineLimitDisplay.value,
      })
    : t("PAYJOY_NO_CARD_BANNER_DESCRIPTION")
);
const payjoyCardBannerCta = computed(() =>
  isCardAvailable.value ? t("PAYJOY_CARD_BANNER_CTA") : ""
);
const payjoyCardBannerImage = computed(() =>
  isCardAvailable.value
    ? useAsset("img/merchant-console/payjoy-card/payjoy-card-preview-sm.svg")
    : useAsset("img/merchant-console/payjoy-card/payjoy-no-card-preview-sm.svg")
);
const payjoyCardBannerColor = computed(() =>
  isCardAvailable.value ? "card-available-color" : "card-not-available-color"
);
</script>
<style scoped lang="scss">
@import "@/assets/scss/_constants.scss";
.pj-card-banner-regular-pointer {
  cursor: pointer;
}
.pj-card-banner-container {
  align-items: center;
  cursor: pointer;
  padding: 0;
  border-radius: 8px;
}
.pj-card-preview img {
  width: 90px;
  @media screen and (max-width: $mobile) {
    width: 70px;
  }
}
.card-available-color {
  color: $pj-color-green;
  background-color: $pj-color-honeysuckle;
}
.card-not-available-color {
  color: #595a5a;
  background-color: #dddddd;
}
.pj-card-promo-details h5 {
  @media screen and (max-width: $mobile) {
    font-size: 15px;
  }
}
.pj-card-promo-details p {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
}
.pj-card-promo-details .banner-cta {
  margin-right: 10px;
  @media screen and (max-width: $mobile) {
    float: right;
  }
}
</style>
